import { useEffect, useState, useRef, useCallback } from "react";
import axios, { AxiosError, CancelTokenSource } from "axios";
import {
  applyFiltersToURL,
  convertToWildcardFormat,
} from "../util/filterStateManager";
import { decodeNumericFilterCriterion } from "../components/Filters/filterutils";
import { ADVANCED_FILTERS_KEY } from "../components/AdvancedFilters/advancedFiltersUtils";
import type { AdvancedFilterCard } from "../types/types";

export const can_use_alternate_request = (filters: {
  [key: string]: string[];
}) => {
  const filters_str = applyFiltersToURL({
    params: new URLSearchParams(),
    filtersFromUrl: filters,
  }).toString();
  const filters_size = new TextEncoder().encode(filters_str).length;
  return filters_size > 2000;
};

/**
 * Makes a POST request with filter state, applying wildcard/exclude conversion
 * when `availableFilters` metadata is provided.
 *
 * `availableFilters` — pass the full set of filter options from the metadata
 * endpoint when you need wildcard conversion (e.g. many filters selected → "*").
 * When provided but still loading (empty object), the hook automatically defers
 * the request until metadata arrives to prevent sending oversized POST bodies
 * that can trigger 502 errors on page refresh. Omit entirely for callers that
 * don't need wildcard conversion (e.g. fetching the filter sidebar options).
 */
export function useAlternateRequest<TData>({
  filters,
  filterLabels,
  url,
  canFetch = true,
  availableFilters,
}: {
  filters: { [key: string]: string[] };
  filterLabels: string[];
  url?: string;
  canFetch?: boolean;
  /** Full filter option metadata. When provided, enables wildcard conversion and
   *  defers the request until metadata has loaded (non-empty). Omit if wildcard
   *  conversion is not needed for this endpoint. */
  availableFilters?: {
    [filterType: string]: { name: string; id?: string | null; type?: string }[];
  };
}) {
  const [data, setData] = useState<TData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AxiosError>();
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const lastRequestRef =
    useRef<{ filters: { [key: string]: string[] }; url: string } | null>(null);

  const fetchData = useCallback(async () => {
    // Cancel any existing request
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("New request initiated");
    }

    // Create new cancel token
    const cancelToken = axios.CancelToken.source();
    cancelTokenRef.current = cancelToken;

    setIsLoading(true);
    setError(undefined);

    try {
      const filtersToSend: { [key: string]: string[] } = {};
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        const isNumericFilterValue =
          value.length === 1 && !!decodeNumericFilterCriterion(value[0]);
        const isAdvancedFiltersValue = key === ADVANCED_FILTERS_KEY;
        if (
          isNumericFilterValue ||
          isAdvancedFiltersValue ||
          filterLabels.includes(key)
        ) {
          filtersToSend[key] = value;
        } else {
          value.forEach((item) => {
            searchParams.append(key, item);
          });
        }
      });

      // Convert to new API format with wildcard and exclude support
      const {
        filters: newFormatFilters,
        exclude_filters,
        numeric_query_params,
        advanced_filters,
      } = convertToWildcardFormat(filtersToSend, availableFilters ?? {});

      // Build request body with new format
      const requestBody: {
        filters: Record<string, string[] | "*">;
        exclude_filters?: Record<string, string[]>;
        advanced_filters?: AdvancedFilterCard[];
      } = {
        filters: newFormatFilters,
      };
      if (exclude_filters) {
        requestBody.exclude_filters = exclude_filters;
      }

      // AP-1016 (Advanced Filters, Part 1) — per the backend's own Part 1
      // plan (docs/plans/AP-1016-advanced-filters-part1-BE-plan.md),
      // numeric filters are flat query-string params (`<type>_min`/
      // `<type>_max`), not part of the JSON body — append them to the same
      // `searchParams` used for pagination/sort, not `requestBody`.
      if (numeric_query_params) {
        Object.entries(numeric_query_params).forEach(([key, value]) => {
          searchParams.append(key, value);
        });
      }
      if (advanced_filters) {
        requestBody.advanced_filters = advanced_filters;
      }

      const { data: responseData } = await axios.post<TData>(
        url + "?" + searchParams.toString(),
        requestBody,
        {
          cancelToken: cancelToken.token,
        }
      );
      setData(responseData);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setError(error as AxiosError);
      }
    } finally {
      setIsLoading(false);
      cancelTokenRef.current = null;
    }
  }, [filterLabels, filters, url, availableFilters]);

  const mutate = useCallback(
    async (data?: TData) => {
      if (data !== undefined) {
        setData(data);
      }
      await fetchData();
    },
    [fetchData]
  );

  useEffect(() => {
    // Check if filters or URL have actually changed
    const hasChanged =
      !lastRequestRef.current ||
      JSON.stringify(lastRequestRef.current.filters) !==
        JSON.stringify(filters) ||
      lastRequestRef.current.url !== url;

    // When availableFilters was explicitly provided but is still empty, the
    // metadata hasn't loaded yet. Defer until it arrives so convertToWildcardFormat
    // can compress large selections to "*" rather than sending all raw strings,
    // which would produce an oversized POST body and risk 502s on page refresh.
    // Once metadata loads, fetchData ref changes and this effect re-runs.
    if (
      availableFilters !== undefined &&
      can_use_alternate_request(filters) &&
      Object.keys(availableFilters).length === 0
    ) {
      return;
    }

    if (hasChanged && canFetch && url) {
      lastRequestRef.current = { filters, url };
      fetchData();
    }
  }, [filters, url, fetchData, canFetch, filterLabels, availableFilters]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
    };
  }, []);

  return { data, isLoading, error, mutate };
}
